import { ReactNode, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/lib/ThemeContext';

interface AppShellProps {
  children: ReactNode;
  sidebar: ReactNode;
}

export function AppShell({ children, sidebar }: AppShellProps) {
  const { resolvedTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className={`min-h-screen bg-bg text-text-primary ${resolvedTheme}`}>
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <AnimatePresence mode="wait">
          <motion.aside
            key={sidebarCollapsed ? 'collapsed' : 'expanded'}
            className={`sidebar ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
            initial={{ width: sidebarCollapsed ? '4rem' : '16rem' }}
            animate={{ width: sidebarCollapsed ? '4rem' : '16rem' }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            exit={{ width: sidebarCollapsed ? '16rem' : '4rem' }}
          >
            <div className="flex h-full flex-col">
              <div className="flex h-16 items-center justify-between px-4 border-b border-border">
                {!sidebarCollapsed && (
                  <span className="font-semibold text-lg">ONE-TASK-VIEW</span>
                )}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="btn-icon text-text-secondary hover:text-text-primary"
                  aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin">
                {sidebar}
              </nav>
            </div>
          </motion.aside>
        </AnimatePresence>

        {/* Main content */}
        <main
          className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={sidebarCollapsed ? 'collapsed' : 'expanded'}
              className="flex-1 flex flex-col overflow-hidden"
              initial={{ opacity: 0, x: sidebarCollapsed ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: sidebarCollapsed ? 20 : -20 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}